import React, { useEffect, useState } from "react";
import { studentDetailsMain } from "../../../../api/StudentDetails";
import { GetHostelList } from "../../../../api/HostelService";
import { SweetAlertShow } from "../../../master/Master";
import { imgBaseUrl } from "../../../../api/api";
import usePrivateLink from "../../../config/usePrivateLink";
import { Link, useNavigate } from "react-router-dom";
import PasswordUpdateModal from "./PasswordUpdateModal";

export default function StudentPasswordUpdate({ id }) {
  const hasAccess = usePrivateLink();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showHostelModal, setShowHostelModal] = useState(false);
  const [hostels, setHostels] = useState([]);
  const [hostelLoading, setHostelLoading] = useState(false);
  const [selectedHostelId, setSelectedHostelId] = useState("");

  const getStudentDetailsMain = async (student_id) => {
    setLoading(true);
    try {
      const response = await studentDetailsMain(student_id);
      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getStudentDetailsMain(id);
    }
  }, [id]);

  const getHostelList = async () => {
    setHostelLoading(true);

    try {
      const response = await GetHostelList("");

      if (response?.data?.status) {
        const responseData = response?.data?.data;

        let hostelList = [];

        if (Array.isArray(responseData)) {
          hostelList = responseData;
        } else if (Array.isArray(responseData?.data)) {
          hostelList = responseData.data;
        } else if (Array.isArray(responseData?.data?.data)) {
          hostelList = responseData.data.data;
        }

        setHostels(hostelList);
      } else {
        setHostels([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load hostels.",
        );
      }
    } catch (error) {
      setHostels([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setHostelLoading(false);
    }
  };

  const handleAddToHostel = () => {
    setSelectedHostelId("");
    setShowHostelModal(true);
    getHostelList();
  };

  const handleCloseHostelModal = () => {
    if (hostelLoading) {
      return;
    }

    setShowHostelModal(false);
    setSelectedHostelId("");
  };

  const handleContinueToHostel = () => {
    if (!selectedHostelId) {
      SweetAlertShow("info", "Select Hostel", "Please select a hostel first.");
      return;
    }

    if (!data?.id) {
      SweetAlertShow(
        "error",
        "Student Not Found",
        "Student information could not be loaded.",
      );
      return;
    }

    const batchId = data?.student_batch_info?.batch_id;

    if (!batchId) {
      SweetAlertShow(
        "error",
        "Batch Not Found",
        "The student's active batch could not be found.",
      );
      return;
    }

    setShowHostelModal(false);

    navigate(
      `/hostel/student/add?student_id=${encodeURIComponent(
        data.id,
      )}&batch_id=${encodeURIComponent(
        batchId,
      )}&hostel_id=${encodeURIComponent(selectedHostelId)}`,
    );
  };

  const ActionButton = ({ to, label, icon, modalTarget, onClick }) => {
    if (modalTarget) {
      return (
        <button
          type="button"
          className="col-auto stat-chip rounded-3 p-2 text-center text-white me-2 border-0"
          data-bs-toggle="modal"
          data-bs-target={modalTarget}
        >
          <small className="opacity-75">
            <i className={icon}></i> {label}
          </small>
        </button>
      );
    }

    if (onClick) {
      return (
        <button
          type="button"
          className="col-auto stat-chip rounded-3 p-2 text-center text-white me-2 border-0"
          onClick={onClick}
        >
          <small className="opacity-75">
            <i className={icon}></i> {label}
          </small>
        </button>
      );
    }

    return (
      <Link
        to={to}
        className="col-auto stat-chip rounded-3 p-2 text-center text-white me-2"
      >
        <small className="opacity-75">
          <i className={icon}></i> {label}
        </small>
      </Link>
    );
  };

  if (loading) {
    return (
      <>
        <style>{`
          @keyframes shimmerSweep {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .skeleton-block {
            position: relative;
            overflow: hidden;
            background: rgba(255, 255, 255, 0.18);
            border-radius: 8px;
          }
          .skeleton-block::after {
            content: "";
            position: absolute;
            inset: 0;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255, 255, 255, 0.35),
              transparent
            );
            animation: shimmerSweep 1.6s infinite;
          }
          .skeleton-avatar {
            width: 70px;
            height: 70px;
            border-radius: 50%;
          }
        `}</style>

        <div className="card border-0 shadow-lg rounded-4 mb-3 overflow-hidden">
          <div className="student-hero p-4">
            <div className="row align-items-center g-3">
              <div className="col-lg-1 col-md-2 text-center">
                <div className="skeleton-block skeleton-avatar mx-auto"></div>
              </div>

              <div className="col-lg-9 col-md-6">
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <div
                    className="skeleton-block"
                    style={{ width: "180px", height: "24px" }}
                  ></div>
                  <div
                    className="skeleton-block rounded-pill"
                    style={{ width: "70px", height: "22px" }}
                  ></div>
                </div>

                <div className="mt-3 d-flex flex-wrap gap-4">
                  <div
                    className="skeleton-block"
                    style={{ width: "120px", height: "16px" }}
                  ></div>
                  <div
                    className="skeleton-block"
                    style={{ width: "100px", height: "16px" }}
                  ></div>
                  <div
                    className="skeleton-block"
                    style={{ width: "160px", height: "16px" }}
                  ></div>
                </div>
              </div>

              <div className="col-lg-2">
                <div className="row g-2">
                  <div className="col">
                    <div
                      className="skeleton-block rounded-3"
                      style={{ height: "50px" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="card border-0 shadow-lg rounded-4 mb-3 overflow-hidden">
        <div className="student-hero p-4">
          <div className="row align-items-center g-3">
            <div className="col-lg-1 col-md-2 text-center">
              <img
                src={
                  data?.profile_picture
                    ? `${imgBaseUrl}${data.profile_picture}`
                    : "/files/user.png"
                }
                className="student-hero-img"
                onError={(e) => {
                  e.currentTarget.src = "/files/user.png";
                }}
                alt={data?.name || "Student"}
              />
              {/* {imgBaseUrl + data.profile_picture} */}
            </div>

            <div className="col-lg-7 col-md-6 text-white">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <h3 className="fw-bold mb-0">{data?.name}</h3>

                <span className="badge bg-white text-primary rounded-pill px-3 py-1">
                  <i
                    className="ri-checkbox-blank-circle-fill me-1"
                    style={{ fontSize: "8px" }}
                  ></i>
                  {Number(data?.student_batch_info?.status) === 1
                    ? "Active"
                    : Number(data?.student_batch_info?.status) === 4
                      ? "Complete"
                      : "Inactive"}
                </span>
              </div>

              <div className="mt-2 opacity-90">
                <span className="me-4">
                  <i className="ri-id-card-line me-1"></i>
                  REG : {data?.registration_no}
                </span>

                <span className="me-4">
                  <i className="ri-phone-line me-1"></i>
                  {data?.phone || "-"}
                </span>

                <span>
                  <i className="ri-book-open-line me-1"></i>
                  {data?.student_batch_info?.course?.title} •{" "}
                  {data?.student_batch_info?.batch?.title}
                </span>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="row g-2 justify-content-end">
                {hasAccess("/student/update") && (
                  <ActionButton
                    to={`/student/update?id=${data?.id}`}
                    icon="ri-pencil-line"
                    label="Edit Profile"
                  />
                )}
                {hasAccess("/student/fees/deposit") && (
                  <ActionButton
                    to={`/student/fees/deposit?id=${data?.id}`}
                    icon="ri-money-rupee-circle-line"
                    label="Pay Fees"
                  />
                )}
                {hasAccess("/student/batch/enroll") && (
                  <ActionButton
                    to={`/student/batch/enroll?student_id=${data?.id}`}
                    icon="ri-group-line"
                    label="Add in Batch"
                  />
                )}
                {hasAccess("/student/bus/enroll") && (
                  <ActionButton
                    to={`/student/bus/enroll?student_id=${data?.id}`}
                    icon="ri-bus-line"
                    label="Add to Bus"
                  />
                )}
                {hasAccess("/hostel/student/add") &&
                  // String(data?.hostel_status).toLowerCase() === "yes" &&
                  !data?.hostel_student_id && (
                    <ActionButton
                      onClick={handleAddToHostel}
                      icon="ri-hotel-line"
                      label="Add to Hostel"
                    />
                  )}
                {hasAccess("/student/password/update") && (
                  <button
                    type="button"
                    className="col-auto stat-chip rounded-3 p-2 text-center text-white me-2 border-0"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    <small className="opacity-75">
                      <i className="ri-lock-password-line me-1"></i>
                      Password Update
                    </small>
                  </button>
                )}
                {hasAccess("/student/documents/add") && (
                  <ActionButton
                    to={`/student/documents/add?student_id=${data?.id}`}
                    icon="ri-file-text-line"
                    label="Documents Add"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {showPasswordModal && (
          <PasswordUpdateModal
            studentId={data?.id}
            mobile={data?.phone}
            onClose={() => setShowPasswordModal(false)}
          />
        )}
      </div>
      {showHostelModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
            onClick={handleCloseHostelModal}
          ></div>

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1055 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header border-bottom">
                  <div>
                    <h5 className="modal-title mb-1">
                      <i className="ri-hotel-line me-2 text-primary"></i>
                      Add Student to Hostel
                    </h5>

                    <small className="text-muted">
                      Select the hostel where this student will stay.
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseHostelModal}
                    aria-label="Close"
                    disabled={hostelLoading}
                  ></button>
                </div>

                <div className="modal-body p-4">
                  <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-primary mb-4">
                    <div
                      className="rounded-circle overflow-hidden flex-shrink-0 border"
                      style={{
                        width: 52,
                        height: 52,
                      }}
                    >
                      <img
                        src={
                          data?.profile_picture
                            ? `${imgBaseUrl}${data.profile_picture}`
                            : "/files/user.png"
                        }
                        alt={data?.name || "Student"}
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => {
                          e.currentTarget.src = "/files/user.png";
                        }}
                      />
                    </div>

                    <div className="flex-grow-1">
                      <h6 className="mb-1">{data?.name}</h6>

                      <small className="text-muted d-block">
                        REG: {data?.registration_no || "-"}
                      </small>

                      <small className="text-muted">
                        {data?.student_batch_info?.batch?.title || "-"}
                      </small>
                    </div>
                  </div>

                  <div className="form-floating form-floating-outline">
                    <select
                      className="form-select"
                      id="select_hostel"
                      value={selectedHostelId}
                      onChange={(e) => setSelectedHostelId(e.target.value)}
                      disabled={hostelLoading}
                    >
                      <option value="">
                        {hostelLoading
                          ? "Loading hostels..."
                          : hostels.length === 0
                            ? "No hostels found"
                            : "Select Hostel"}
                      </option>

                      {hostels.map((hostel) => (
                        <option key={hostel?.id} value={hostel?.id}>
                          {hostel?.title ||
                            hostel?.name ||
                            hostel?.hostel_name ||
                            `Hostel ${hostel?.id}`}
                        </option>
                      ))}
                    </select>

                    <label htmlFor="select_hostel">Hostel</label>
                  </div>

                  {!hostelLoading && hostels.length === 0 && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <i className="ri-information-line me-1"></i>
                      No hostels found.
                    </div>
                  )}
                </div>

                <div className="modal-footer border-top">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCloseHostelModal}
                    disabled={hostelLoading}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    disabled={hostelLoading || !selectedHostelId}
                    onClick={handleContinueToHostel}
                  >
                    {hostelLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="ri-arrow-right-line me-1"></i>
                        Continue
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
